import { RichTextEditor } from '@juvare/common-form-ui/RichTextEditor';
import { FormHelperErrorDescription } from '@juvare/common-form-ui/WidgetForm/Components';
import { getVisibleError, handleWidgetUpdate, isSkipValidate, renderForm, validateCustom } from '@juvare/common-form-ui/WidgetForm/Utils';
import { WidgetHandler } from '@juvare/common-form-ui/WidgetHandler';
import { showAlertDialog } from '@juvare/common-web-ui/DialogHelper';
import { themeColors } from '@juvare/common-web-ui/JuvareTheme';
import { BaseWidgetEditorComponent } from '@juvare/widget-ui/BaseWidgetEditorComponent';
import ForwardToInboxOutlinedIcon from '@mui/icons-material/ForwardToInboxOutlined';
import { Alert, Box, FormControl, FormLabel, Typography } from '@mui/material';
import React from 'react';
import { useAlertUiTranslation } from '../../../../hooks';
import { useAlertAttachments } from '../../hooks';
import AddAttachmentButton from './Components/AddAttachmentButton';
import AttachmentList from './Components/AttachmentList';
import { renderApiErrors } from './Utils/RenderApiErrors';
import { type } from './types';
export const WidgetLongMessage = (props) => {
    var _a, _b, _c, _d;
    const { t } = useAlertUiTranslation();
    const responseOptionsEnabled = ((_a = props.config) === null || _a === void 0 ? void 0 : _a.responseOptionsEnabled) || false;
    const value = props.getData();
    const responsePlaceholder = '${responseUrl}';
    const error = getVisibleError(props);
    const inputFileRef = React.useRef(null);
    const updateFormDataAttachments = (attachments) => {
        var _a;
        props.rootData.emailAttachments = attachments;
        renderForm(props, { changed: true, autoSaveImmediately: (_a = props.config) === null || _a === void 0 ? void 0 : _a.autoSave });
    };
    const { attachments, isLoading, loadingEmailAttachments, error: attachmentError, handleSaveAttachments, handleScanAttachments, handleGetAttachment, handleRemoveAttachment, } = useAlertAttachments(props.rootData.emailAttachments, updateFormDataAttachments);
    React.useEffect(() => {
        if (attachmentError) {
            showAlertDialog({ title: t('pages.edit.validationFailed'), content: renderApiErrors(attachmentError) });
        }
    }, [t, attachmentError]);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const init = {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        setup: (editor) => {
            editor.ui.registry.addButton('addPlaceholderButton', {
                text: t('pages.edit.richTextEditors.responsePlaceholder'),
                onAction: () => editor.insertContent(responsePlaceholder),
                tooltip: t('pages.edit.richTextEditors.responsePlaceholderTooltip'),
            });
            editor.ui.registry.addIcon('attachment', '<svg height="24" width="24"><path d="M2 12.5C2 9.46 4.46 7 7.5 7H18c2.21 0 4 1.79 4 4s-1.79 4-4 4H9.5C8.12 15 7 13.88 7 12.5S8.12 10 9.5 10H17v2H9.41c-.55 0-.55 1 0 1H18c1.1 0 2-.9 2-2s-.9-2-2-2H7.5C5.57 9 4 10.57 4 12.5S5.57 16 7.5 16H17v2H7.5C4.46 18 2 15.54 2 12.5z"/></svg>');
            editor.ui.registry.addButton('addAttachmentButton', {
                icon: 'attachment',
                onAction: () => inputFileRef.current && inputFileRef.current.click(),
            });
        },
    };
    return (React.createElement(FormControl, { variant: 'outlined', fullWidth: true, required: ((_b = props.config.validate) === null || _b === void 0 ? void 0 : _b.required) || false, error: !!error, sx: {
            '& .tox-tinymce': {
                // unable to overwrite tinyMCE editor styles by passing props
                border: `1px solid ${!!error ? themeColors.accent.red.bold : 'rgba(0, 0, 0, 0.23)'}`, // rgba(0, 0, 0, 0.23) is MUI default border color
                borderRadius: 1,
            },
        } },
        React.createElement(AddAttachmentButton, { attachments: attachments, loadingAttachmentUpload: isLoading, onSaveAttachments: handleSaveAttachments, inputFileRef: inputFileRef }),
        React.createElement(FormLabel, null, props.config.label),
        React.createElement(RichTextEditor, { data: value || props.config.defaultValue, disabled: (_c = props.config) === null || _c === void 0 ? void 0 : _c.disabled, onEditorChange: (htmlMessage) => {
                var _a;
                props.setData(htmlMessage);
                renderForm(props, { changed: true, autoSaveImmediately: (_a = props.config) === null || _a === void 0 ? void 0 : _a.autoSave });
            }, editorInit: init }),
        React.createElement(FormHelperErrorDescription, { error: error }),
        responseOptionsEnabled && (!props.rootData.htmlMessage || !((_d = props.rootData.htmlMessage) === null || _d === void 0 ? void 0 : _d.includes(responsePlaceholder))) && (React.createElement(Box, { pt: 1 },
            React.createElement(Alert, { severity: 'info' },
                React.createElement(Typography, { variant: 'inherit', sx: { fontWeight: 400 } }, t('pages.edit.richTextEditors.responsePlaceholderText', {
                    placeholder: responsePlaceholder,
                }))))),
        React.createElement(AttachmentList, { showActions: true, emailAttachments: attachments, loadingEmailAttachments: loadingEmailAttachments, onRemoveAttachment: handleRemoveAttachment, onGetAttachment: handleGetAttachment, onScanAttachments: handleScanAttachments, loadingAttachmentsUpload: isLoading })));
};
const WidgetLongMessageEditor = (props) => {
    return (React.createElement(BaseWidgetEditorComponent, Object.assign({}, props),
        React.createElement(WidgetLongMessage, Object.assign({}, props))));
};
export const widgetLongMessage = {
    type,
    createWidget: () => ({
        type,
        key: 'longMessage',
        label: 'Long Message',
    }),
    validate: (props) => {
        var _a, _b;
        if (isSkipValidate(props)) {
            return;
        }
        const value = props.getDataStringOrUndefined();
        if ((_a = props.config.validate) === null || _a === void 0 ? void 0 : _a.required) {
            if (!value) {
                return (_b = props.config.validate.requiredMessage) !== null && _b !== void 0 ? _b : (props.config.label ? props.config.label + ' is required' : 'Required');
            }
        }
        return validateCustom(props, value);
    },
    update: (props) => {
        const handler = new WidgetHandler(props);
        const prevData = handler.config.responseOptionsEnabled;
        props.config.responseOptionsEnabled = props.rootData.allowResponse;
        return prevData !== props.config.responseOptionsEnabled || handleWidgetUpdate(props);
    },
    component: WidgetLongMessage,
    icon: React.createElement(ForwardToInboxOutlinedIcon, null),
    editorComponent: WidgetLongMessageEditor,
};
